// Co-assignment helpers — "who else films this artist?" and "does this
// assignment carry a producer note?".
//
// Both are derived purely from existing `crew_assignments` data: the
// canonical performance identity is the `lineup_id` FK (with the same
// stage+artist+date text fallback used everywhere else for backfill
// races), and crew identity reuses `crewIdentityKey`. No extra columns,
// no denormalised "group id" to keep in sync — the lineup slot already
// IS the shared identity for a performance, and RLS already exposes
// every assignment in the briefing to the crew (migration
// 20260402100000_fix_crew_view_other_members_assignments).

import { crewIdentityKey, type CrewIdentityRow } from "./crewIdentity";
import type { LineupBoundRow } from "./stageMatching";

/** Crew assignment row, narrowed to the fields these helpers touch. */
export type CoFilmingAssignment = LineupBoundRow & {
  id?: string | null;
  crew_member_id?: string | null;
  assignment_type?: string | null;
  filming_start?: string | null;
  filming_end?: string | null;
  notes?: string | null;
};

/** A `crew_members` row carrying enough to name + identify the person. */
export type CoAssigneeCrewRow = CrewIdentityRow & {
  id: string;
  name?: string | null;
};

/** One other crew member scheduled on the same performance. */
export type CoAssignee = {
  crewMemberId: string | null;
  name: string;
  filmingStart: string | null;
  filmingEnd: string | null;
};

/**
 * True when the assignment carries a non-empty producer note. Drives the
 * "has note" indicator on the assignment card.
 */
export function hasAssignmentNote(
  assignment: { notes?: string | null } | null | undefined,
): boolean {
  return !!assignment && typeof assignment.notes === "string" && assignment.notes.trim().length > 0;
}

/**
 * True when two assignment rows refer to the SAME artist performance —
 * irrespective of which crew member is assigned, and irrespective of the
 * exact filming time (one crew member may film a quarter-hour earlier
 * than another on the same set).
 *
 * Deliberately different from `assignmentsLikelySame`, which short-
 * circuits on row `id` (it answers "is this the same row?"). Two crew
 * members filming one artist have distinct assignment ids but the same
 * performance, so we never look at `id` here.
 *
 * Only artist-type assignments group; custom/break blocks never do.
 */
export function samePerformance(a: CoFilmingAssignment, b: CoFilmingAssignment): boolean {
  if ((a.assignment_type ?? "artist") !== "artist") return false;
  if ((b.assignment_type ?? "artist") !== "artist") return false;

  // FK-first: the lineup slot is the canonical performance identity.
  if (a.lineup_id && b.lineup_id) return a.lineup_id === b.lineup_id;

  // Fallback for rows whose lineup_id hasn't backfilled yet: must share
  // the date + a non-empty artist, plus the stage (FK when both present,
  // else the text name).
  if (a.event_date !== b.event_date) return false;
  const artistA = (a.artist_name ?? "").trim().toLowerCase();
  const artistB = (b.artist_name ?? "").trim().toLowerCase();
  if (!artistA || artistA !== artistB) return false;
  if (a.stage_id && b.stage_id) return a.stage_id === b.stage_id;
  return (a.stage_name ?? "") === (b.stage_name ?? "");
}

/**
 * Find every OTHER crew member scheduled on the same performance as
 * `target`, with their own filming times. Deduped by crew identity (a
 * person's multi-day rows collapse to one), sorted by filming start.
 *
 * `target`'s own rows are excluded by crew identity, so a crew member
 * who happens to have two rows on the same set never lists themselves.
 */
export function findCoAssignees(
  target: CoFilmingAssignment,
  allAssignments: CoFilmingAssignment[],
  crewMembers: CoAssigneeCrewRow[],
): CoAssignee[] {
  if ((target.assignment_type ?? "artist") !== "artist") return [];

  const byId = new Map<string, CoAssigneeCrewRow>();
  for (const member of crewMembers) byId.set(member.id, member);

  // Stable identity for a crew_member_id: prefer the canonical crew
  // identity key, fall back to the raw id so unmapped rows still dedupe.
  const identityOf = (crewMemberId?: string | null): string | null => {
    if (!crewMemberId) return null;
    const row = byId.get(crewMemberId);
    return row ? crewIdentityKey(row) : `cm:${crewMemberId}`;
  };

  const targetIdentity = identityOf(target.crew_member_id);
  const seen = new Set<string>();
  const result: CoAssignee[] = [];

  for (const other of allAssignments) {
    if (target.id && other.id && other.id === target.id) continue;
    if (!samePerformance(target, other)) continue;

    const otherIdentity = identityOf(other.crew_member_id);
    if (otherIdentity && targetIdentity && otherIdentity === targetIdentity) continue;

    const dedupeKey = otherIdentity ?? `row:${other.id ?? ""}`;
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);

    const row = other.crew_member_id ? byId.get(other.crew_member_id) : undefined;
    result.push({
      crewMemberId: other.crew_member_id ?? null,
      name: (row?.name ?? "").trim() || "Unknown",
      filmingStart: other.filming_start ?? null,
      filmingEnd: other.filming_end ?? null,
    });
  }

  result.sort((a, b) => (a.filmingStart ?? "").localeCompare(b.filmingStart ?? ""));
  return result;
}

/**
 * Names of every crew member scheduled to film a given performance —
 * used by the lineup ("Scheduled for: …"). Unlike `findCoAssignees`,
 * this does NOT exclude anyone: it lists ALL shooters on the slot.
 *
 * `slot` is a performance-shaped row (a lineup row works: pass its `id`
 * as `lineup_id` plus the artist/stage/date). Deduped by crew identity
 * and sorted by filming start, then name.
 */
export function crewScheduledForPerformance(
  slot: CoFilmingAssignment,
  allAssignments: CoFilmingAssignment[],
  crewMembers: CoAssigneeCrewRow[],
): string[] {
  const byId = new Map<string, CoAssigneeCrewRow>();
  for (const member of crewMembers) byId.set(member.id, member);

  const identityOf = (crewMemberId?: string | null): string | null => {
    if (!crewMemberId) return null;
    const row = byId.get(crewMemberId);
    return row ? crewIdentityKey(row) : `cm:${crewMemberId}`;
  };

  const seen = new Set<string>();
  const result: { name: string; start: string | null }[] = [];

  for (const a of allAssignments) {
    if (!samePerformance(slot, a)) continue;
    const dedupeKey = identityOf(a.crew_member_id) ?? `row:${a.id ?? ""}`;
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    const row = a.crew_member_id ? byId.get(a.crew_member_id) : undefined;
    result.push({
      name: (row?.name ?? "").trim() || "Unknown",
      start: a.filming_start ?? null,
    });
  }

  result.sort(
    (x, y) => (x.start ?? "").localeCompare(y.start ?? "") || x.name.localeCompare(y.name),
  );
  return result.map((r) => r.name);
}
